import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Form, Row, Col, Input, Select, DatePicker, Button, Collapse, Empty, Spin, Checkbox, message } from 'antd';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import moment from 'moment';
import { 
  getCompanies, 
  getBranchOffices,
  getCollections,
  getApprovedTexts,
  getEvaluationTypes,
  getCostsCenters,
  getApplicants,
  getUser
} from '../../services/general';
import { getAllBlocks } from '../../services/reservation';
import { getCookie } from '../../services/cookies';
import './unifiedSearchFilters.less';
import locale from 'antd/es/date-picker/locale/es_ES';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;

const UnifiedSearchFilters = ({ 
  form: externalForm,
  baseFilters = ['company', 'outsourcing', 'thirdCompany', 'branchOffice', 'protocol', 'aptitude', 'dateRange', 'workerId', 'workerName'],
  customFilters = [],
  customNodes = [],
  isCollapsible = true,
  moduleName = 'Personalizados',
  onSearch,
  onClear,
  initialValues = {},
  userHasSystemWideAccess = false,
  loading = false,
}) => {
  const [internalForm] = Form.useForm();
  const form = externalForm || internalForm;
  const { t } = useTranslation();
  
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [outsourcings, setOutsourcings] = useState([]);
  const [loadingOutsourcings, setLoadingOutsourcings] = useState(false);
  const [thirdCompanies, setThirdCompanies] = useState([]);
  const [loadingThirdCompanies, setLoadingThirdCompanies] = useState(false);
  const [branchOffices, setBranchOffices] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [protocols, setProtocols] = useState([]);
  const [loadingProtocols, setLoadingProtocols] = useState(false);
  const [aptitudes, setAptitudes] = useState([]);
  const [loadingAptitudes, setLoadingAptitudes] = useState(false);
  const [evaluationTypes, setEvaluationTypes] = useState([]);
  const [loadingEvaluationTypes, setLoadingEvaluationTypes] = useState(false);
  const [costsCenters, setCostsCenters] = useState([]);
  const [loadingCostsCenters, setLoadingCostsCenters] = useState(false);
  const [applicants, setApplicants] = useState([]);
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const [timeBlocks, setTimeBlocks] = useState([]);
  const [loadingTimeBlocks, setLoadingTimeBlocks] = useState(false);
  
  const selectedCompanyId = Form.useWatch('company_id', form);
  const outsourcingCompanyId = Form.useWatch('outsourcing_id', form);
  const thirdCompanyId = Form.useWatch('third_company_id', form);

  const fetchProtocols = useCallback(async (companyId, outsourcingId, thirdId) => {
    setLoadingProtocols(true);
    try {
      const params = {};
      if (companyId) params.company_id = companyId;
      if (outsourcingId) params.outsourcing_id = outsourcingId;
      if (thirdId) params.third_party_id = thirdId;
      const response = await getCollections(params);
      setProtocols(response?.data || response);
    } catch (error) {
      console.error(error);
      message.error(t('unifiedSearchFilters.errors.fetchProtocols'));
    } finally {
      setLoadingProtocols(false);
    }
  }, [t]);

  const fetchAptitudes = useCallback(async () => {
    setLoadingAptitudes(true);
    try {
      const params = { country_code: getCookie('country_code') };
      const response = await getApprovedTexts(params);
      setAptitudes(response?.data || response);
    } catch (error) {
      console.error(error);
      message.error(t('unifiedSearchFilters.errors.fetchAptitudes'));
    } finally {
      setLoadingAptitudes(false);
    }
  }, [t]);

  const fetchEvaluationTypes = useCallback(async () => {
    setLoadingEvaluationTypes(true);
    try {
      const response = await getEvaluationTypes();
      setEvaluationTypes(response?.data || response);
    } catch (error) {
      console.error(error);
      message.error(t('unifiedSearchFilters.errors.fetchEvaluationTypes'));
    } finally {
      setLoadingEvaluationTypes(false);
    }
  }, [t]);

  useEffect(() => {
    if (baseFilters.includes('branchOffice') || customFilters.includes('branchOffice')) {
      fetchBranchOffices();
    }
    if (baseFilters.includes('company') || customFilters.includes('company')) {
      fetchCompaniesSearch('');
    }
    if (baseFilters.includes('consultationResult') || customFilters.includes('consultationResult')) {
      fetchAptitudes();
    }
    if (baseFilters.includes('evaluationType') || customFilters.includes('evaluationType')) {
      fetchEvaluationTypes();
    }
    if (baseFilters.includes('timeBlock') || customFilters.includes('timeBlock')) {
      fetchTimeBlocks();
    }
    
    const hasDateRange = baseFilters.includes('dateRange') || customFilters.includes('dateRange');
    const fieldsToSet = { ...initialValues };
    if (hasDateRange) {
      fieldsToSet.dates = initialValues.dates || [moment(), moment()];
    }
    form.setFieldsValue(fieldsToSet);

    if (initialValues.user_id) {
      fetchInitialUser(initialValues.user_id);
    }
  }, []);

  const fetchInitialUser = async (userId) => {
    try {
      const response = await getUser(userId);
      const data = response?.data || response;
      const userData = Array.isArray(data) ? data[0] : data;
      
      if (userData && userData.id) {
        const fullname = userData.fullname || 
                         (userData.first_name ? `${userData.first_name} ${userData.last_name}` : 
                         userData.names ? `${userData.names} ${userData.last_name}` : 
                         String(userData.id));
        
        setApplicants([{ id: userData.id, fullname: fullname.trim() }]);
      }
    } catch (error) {
      console.error('Error fetching initial user:', error);
      message.error(t('unifiedSearchFilters.errors.fetchInitialUser'));
    }
  };

  const fetchApplicantsSearch = useMemo(() => debounce(async (query) => {
    if (!query) return;
    setLoadingApplicants(true);
    try {
      const response = await getApplicants({ query, limit: 50 });
      setApplicants(response?.data || response);
    } catch (error) {
      console.error(error);
      message.error(t('unifiedSearchFilters.errors.fetchApplicants', 'Error al buscar solicitante'));
    } finally {
      setLoadingApplicants(false);
    }
  }, 500), [t]);

  const fetchCostsCenters = useCallback(async (companyId) => {
    if (!companyId) {
      setCostsCenters([]);
      return;
    }
    setLoadingCostsCenters(true);
    try {
      const response = await getCostsCenters({ companyId, limit: 50 });
      setCostsCenters(response?.data || response);
    } catch (error) {
      console.error(error);
      message.error(t('unifiedSearchFilters.errors.fetchCostCenters', 'Error al cargar centros de costos'));
    } finally {
      setLoadingCostsCenters(false);
    }
  }, [t]);

  const fetchTimeBlocks = useCallback(async () => {
    setLoadingTimeBlocks(true);
    try {
      const response = await getAllBlocks();
      setTimeBlocks(response?.data || response);
    } catch (error) {
      console.error(error);
      message.error(t('unifiedSearchFilters.errors.fetchTimeBlocks', 'Error al cargar bloques de horario'));
    } finally {
      setLoadingTimeBlocks(false);
    }
  }, [t]);

  useEffect(() => {
    if (baseFilters.includes('costCenter') || customFilters.includes('costCenter')) {
      fetchCostsCenters(selectedCompanyId);
      form.setFieldsValue({ cost_center_id: undefined });
    }
  }, [selectedCompanyId, fetchCostsCenters, JSON.stringify(baseFilters), JSON.stringify(customFilters)]);

  const fetchCompaniesSearch = useMemo(() => debounce(async (query) => {
    setLoadingCompanies(true);
    try {
      const params = { limit: 50 };
      if (query) params.query = query;
      const response = await getCompanies(params);
      setCompanies(response?.data || response);
    } catch (error) {
      console.error(error);
      message.error(t('unifiedSearchFilters.errors.fetchCompanies'));
    } finally {
      setLoadingCompanies(false);
    }
  }, 500), [t]);

  const fetchOutsourcingsSearch = useMemo(() => debounce(async (query) => {
    setLoadingOutsourcings(true);
    try {
      const params = { limit: 50 };
      if (query) params.query = query;
      const response = await getCompanies(params);
      setOutsourcings(response?.data || response);
    } catch (error) {
      console.error(error);
      message.error(t('unifiedSearchFilters.errors.fetchOutsourcings'));
    } finally {
      setLoadingOutsourcings(false);
    }
  }, 500), [t]);

  const fetchThirdCompaniesSearch = useMemo(() => debounce(async (query) => {
    setLoadingThirdCompanies(true);
    try {
      const params = { limit: 50 };
      if (query) params.query = query;
      const response = await getCompanies(params);
      setThirdCompanies(response?.data || response);
    } catch (error) {
      console.error(error);
      message.error(t('unifiedSearchFilters.errors.fetchThirdCompanies'));
    } finally {
      setLoadingThirdCompanies(false);
    }
  }, 500), [t]);

  const fetchBranchOffices = async () => {
    setLoadingBranches(true);
    try {
      const response = await getBranchOffices({ byUser: 1 });
      setBranchOffices(response || []);
    } catch (error) {
      console.error(error);
      message.error(t('unifiedSearchFilters.errors.fetchBranchOffices'));
    } finally {
      setLoadingBranches(false);
    }
  };

  useEffect(() => {
    if (baseFilters.includes('outsourcing') || customFilters.includes('outsourcing')) {
      fetchOutsourcingsSearch('');
    }
    if (baseFilters.includes('thirdCompany') || customFilters.includes('thirdCompany')) {
      fetchThirdCompaniesSearch('');
    }
  }, [fetchOutsourcingsSearch, fetchThirdCompaniesSearch, JSON.stringify(baseFilters), JSON.stringify(customFilters)]);

  useEffect(() => {
    if (baseFilters.includes('protocol') || customFilters.includes('protocol')) {
      fetchProtocols(selectedCompanyId, outsourcingCompanyId, thirdCompanyId);
    }
  }, [fetchProtocols, selectedCompanyId, outsourcingCompanyId, thirdCompanyId, JSON.stringify(baseFilters), JSON.stringify(customFilters)]);

  const handleFinish = (values) => {
    const hasDateRange = baseFilters.includes('dateRange') || customFilters.includes('dateRange');
    const payload = {
      ...values,
      start_date: (hasDateRange && values.dates) ? values.dates[0].format('YYYY-MM-DD') : undefined,
      end_date: (hasDateRange && values.dates) ? values.dates[1].format('YYYY-MM-DD') : undefined,
    };
    delete payload.dates;
    
    const cleanedPayload = Object.fromEntries(
      Object.entries(payload).filter(([_, v]) => v !== undefined && v !== null && v !== "")
    );

    if (onSearch) onSearch(cleanedPayload);
  };

  const handleClear = () => {
    form.resetFields();
    const hasDateRange = baseFilters.includes('dateRange') || customFilters.includes('dateRange');
    const hasBranchOffice = baseFilters.includes('branchOffice') || customFilters.includes('branchOffice');
    
    const fieldsToSet = {};
    if (hasDateRange) {
      fieldsToSet.dates = [moment(), moment()];
    }
    if (hasBranchOffice) {
      const branchId = getCookie('branch_office_id');
      if (branchId) {
        fieldsToSet.branch_office_id = parseInt(branchId);
      }
    }
    form.setFieldsValue(fieldsToSet);
    if (onClear) onClear();
  };

  const emptyState = (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        <div className="empty-search">
          <div>{t('unifiedSearchFilters.emptyStateTitle')}</div>
          <div>{t('unifiedSearchFilters.emptyStateDescription')}</div>
        </div>
      }
    />
  );

  const renderFilter = (filterKey) => {
    switch (filterKey) {
      case 'company':
        return (
          <Col xl={6} lg={8} md={12} xs={24} key="company">
            <Form.Item name="company_id" label={t('unifiedSearchFilters.company')}>
              <Select
                className="cmt-select" size="large"
                placeholder={t('unifiedSearchFilters.searchPlaceholder')}
                showSearch
                allowClear
                filterOption={false}
                onSearch={fetchCompaniesSearch}
                notFoundContent={loadingCompanies ? <Spin size="small" /> : emptyState}
              >
                {companies.map(company => <Option key={company.id} value={company.id}>{company.business_name}</Option>)}
              </Select>
            </Form.Item>
          </Col>
        );
      case 'outsourcing':
        return (
          <Col xl={6} lg={8} md={12} xs={24} key="outsourcing">
            <Form.Item name="outsourcing_id" label={t('unifiedSearchFilters.outsourcing')}>
              <Select
                className="cmt-select" size="large"
                placeholder={t('unifiedSearchFilters.searchPlaceholder')}
                showSearch
                allowClear
                filterOption={false}
                onSearch={fetchOutsourcingsSearch}
                notFoundContent={loadingOutsourcings ? <Spin size="small" /> : emptyState}
              >
                {outsourcings.map(company => <Option key={company.id} value={company.id}>{company.business_name}</Option>)}
              </Select>
            </Form.Item>
          </Col>
        );
      case 'thirdCompany':
        return (
          <Col xl={6} lg={8} md={12} xs={24} key="thirdCompany">
            <Form.Item name="third_company_id" label={t('unifiedSearchFilters.thirdCompany')}>
              <Select
                className="cmt-select" size="large"
                placeholder={t('unifiedSearchFilters.searchPlaceholder')}
                showSearch
                allowClear
                filterOption={false}
                onSearch={fetchThirdCompaniesSearch}
                notFoundContent={loadingThirdCompanies ? <Spin size="small" /> : emptyState}
              >
                {thirdCompanies.map(company => <Option key={company.id} value={company.id}>{company.business_name}</Option>)}
              </Select>
            </Form.Item>
          </Col>
        );
      case 'branchOffice':
        return (
          <Col xl={6} lg={8} md={12} xs={24} key="branchOffice">
            <Form.Item name="branch_office_id" label={t('unifiedSearchFilters.branchOffice')}>
              <Select
                className="cmt-select" size="large"
                placeholder={t('unifiedSearchFilters.searchPlaceholder')}
                showSearch
                allowClear
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                notFoundContent={loadingBranches ? <Spin size="small" /> : emptyState}
              >
                {branchOffices.map(branch => <Option key={branch.id} value={branch.id}>{branch.name}</Option>)}
              </Select>
            </Form.Item>
          </Col>
        );
      case 'allBranchOffices':
        const label = userHasSystemWideAccess ? t('unifiedSearchFilters.showAllBranchOffices') : t('unifiedSearchFilters.showMyBranchOffices');
        return (
          <Col xl={6} lg={8} md={12} xs={24} key="allBranchOffices" className="mt-30">
            <Row align="middle">
              <Col>
                <Form.Item name="allBranchOffices" valuePropName="checked" className="mb-0">
                  <Checkbox className="cmt-checkbox" />
                </Form.Item>
              </Col>
              <Col>
                <p className="ml-10 mb-0">{label}</p>
              </Col>
            </Row>
          </Col>
        );
      case 'dateRange':
        return (
          <Col xl={6} lg={8} md={12} xs={24} key="dateRange">
            <Form.Item name="dates" label={t('unifiedSearchFilters.dateRange')}>
              <RangePicker
                className="range_picker w-100" size="large"
                format="DD-MM-YYYY"
                locale={locale}
                allowClear
                
              />
            </Form.Item>
          </Col>
        );
      case 'workerId':
        return (
          <Col xl={6} lg={8} md={12} xs={24} key="workerId">
            <Form.Item name="patient_document_number" label={t('unifiedSearchFilters.workerId')}>
              <Input className="cmt-input" size="large" placeholder={t('unifiedSearchFilters.workerIdPlaceholder')} allowClear />
            </Form.Item>
          </Col>
        );
      case 'workerName':
        return (
          <Col xl={6} lg={8} md={12} xs={24} key="workerName">
            <Form.Item name="patient_names" label={t('unifiedSearchFilters.workerName')}>
              <Input className="cmt-input" size="large" placeholder={t('unifiedSearchFilters.workerNamePlaceholder')} allowClear />
            </Form.Item>
          </Col>
        );
      case 'reservationNumber':
        return (
          <Col xl={6} lg={8} md={12} xs={24} key="reservationNumber">
            <Form.Item 
              name="reservation_number" 
              label={t('unifiedSearchFilters.reservationNumber')}
              rules={[
                {
                  pattern: /^R\d+$/i,
                  message: t('unifiedSearchFilters.reservationNumberPatternError')
                }
              ]}
            >
              <Input className="cmt-input" size="large" placeholder={t('unifiedSearchFilters.reservationNumberPlaceholder')} allowClear />
            </Form.Item>
          </Col>
        );
      case 'protocol':
        return (
          <Col xl={6} lg={8} md={12} xs={24} key="protocol">
            <Form.Item name="protocol_id" label={t('unifiedSearchFilters.protocol')}>
              <Select
                className="cmt-select" size="large"
                placeholder={t('unifiedSearchFilters.protocolPlaceholder')}
                showSearch
                allowClear
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                notFoundContent={loadingProtocols ? <Spin size="small" /> : emptyState}
              >
                {protocols.map(protocol => <Option key={protocol.id} value={protocol.id}>{protocol.name}</Option>)}
              </Select>
            </Form.Item>
          </Col>
        );
      case 'consultationResult':
        return (
          <Col xl={6} lg={8} md={12} xs={24} key="consultationResult">
            <Form.Item name="consultation_result" label={t('unifiedSearchFilters.aptitude')}>
              <Select
                className="cmt-select" size="large"
                placeholder={t('unifiedSearchFilters.aptitudePlaceholder')}
                showSearch
                allowClear
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                notFoundContent={loadingAptitudes ? <Spin size="small" /> : emptyState}
              >
                {aptitudes.map(aptitude => (
                  <Option key={aptitude.id} value={aptitude.id}>
                    {aptitude.text} - {aptitude.department_id === 1 ? 'Salud' : aptitude.department_id === 2 ? 'Psicología' : 'Otros'}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        );
      case 'evaluationType':
        return (
          <Col xl={6} lg={8} md={12} xs={24} key="evaluationType">
            <Form.Item name="evaluation_types" label={t('unifiedSearchFilters.evaluationType')}>
              <Select 
                mode="multiple" 
                className="cmt-select" size="large" 
                placeholder={t('unifiedSearchFilters.evaluationTypePlaceholder')} 
                allowClear
                loading={loadingEvaluationTypes}
              >
                {evaluationTypes.map((type) => (
                  <Option key={type.id} value={type.code}>
                    {type.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        );
      case 'wayContactForm':
        return (
          <Col xl={6} lg={8} md={12} xs={24} key="wayContactForm">
            <Form.Item name="way_contact_form" label={t('unifiedSearchFilters.contactMethod', 'Medio de contacto')}>
              <Select
                className="cmt-select" size="large"
                placeholder={t('reservations.byFilters.selectReservationMethodPlaceholder', 'Seleccionar medio...')}
                allowClear
              >
                <Option value="way_web">Web</Option>
                <Option value="way_phone">Teléfono</Option>
                <Option value="way_email">Correo electrónico</Option>
              </Select>
            </Form.Item>
          </Col>
        );
      case 'examState':
        return (
          <Col xl={6} lg={8} md={12} xs={24} key="examState">
            <Form.Item name="exam_state" label={t('unifiedSearchFilters.examState')}>
              <Select className="cmt-select" size="large" placeholder={t('unifiedSearchFilters.examStatePlaceholder')} allowClear>
                <Option value="Normal">{t('unifiedSearchFilters.examStates.normal')}</Option>
                <Option value="Alterado">{t('unifiedSearchFilters.examStates.altered')}</Option>
                <Option value="Pendiente">{t('unifiedSearchFilters.examStates.pending')}</Option>
              </Select>
            </Form.Item>
          </Col>
        );
      case 'applicant':
        return (
          <Col xl={6} lg={8} md={12} xs={24} key="applicant">
            <Form.Item name="user_id" label={t('unifiedSearchFilters.applicant', 'Solicitante')}>
              <Select
                className="cmt-select" size="large"
                placeholder={t('unifiedSearchFilters.searchPlaceholder', 'Buscar...')}
                showSearch
                allowClear
                filterOption={false}
                onSearch={fetchApplicantsSearch}
                notFoundContent={loadingApplicants ? <Spin size="small" /> : emptyState}
              >
                {applicants.map(applicant => <Option key={applicant.id} value={applicant.id}>{applicant.fullname}</Option>)}
              </Select>
            </Form.Item>
          </Col>
        );
      case 'costCenter':
        return (
          <Col xl={6} lg={8} md={12} xs={24} key="costCenter">
            <Form.Item name="cost_center_id" label={t('unifiedSearchFilters.costCenter', 'Centro de costo')}>
              <Select
                className="cmt-select" size="large"
                placeholder={t('unifiedSearchFilters.searchPlaceholder', 'Buscar...')}
                showSearch
                allowClear
                disabled={!selectedCompanyId}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                notFoundContent={loadingCostsCenters ? <Spin size="small" /> : emptyState}
              >
                {costsCenters.map(costCenter => <Option key={costCenter.id} value={costCenter.id}>{costCenter.name}</Option>)}
              </Select>
            </Form.Item>
          </Col>
        );
      case 'timeBlock':
        return (
          <Col xl={6} lg={8} md={12} xs={24} key="timeBlock">
            <Form.Item name="block_id" label={t('unifiedSearchFilters.timeBlock', 'Bloque de horario')}>
              <Select
                className="cmt-select" size="large"
                placeholder={t('unifiedSearchFilters.searchPlaceholder', 'Buscar...')}
                showSearch
                allowClear
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                notFoundContent={loadingTimeBlocks ? <Spin size="small" /> : emptyState}
              >
                {timeBlocks.map(block => <Option key={block.id} value={block.id}>{block.start}</Option>)}
              </Select>
            </Form.Item>
          </Col>
        );
      default:
        return null;
    }
  };

  const renderBaseFilters = () => {
    return (
      <Row gutter={[16, 0]}>
        {baseFilters.map(filterKey => renderFilter(filterKey))}
      </Row>
    );
  };

  const renderCustomFilters = () => {
    return (
      <Row gutter={[16, 0]}>
        {customFilters.map(filterKey => renderFilter(filterKey))}
        {customNodes.map((node, i) => React.cloneElement(node, { key: `customNode-${i}` }))}
      </Row>
    );
  };

  const hasCustomFilters = customFilters.length > 0 || customNodes.length > 0;

  return (
    <div className="unified-search-filters">
      <Form form={form} onFinish={handleFinish} layout="vertical" size="large" className='cmt-form'>
        {isCollapsible ? (
          <Collapse defaultActiveKey={[]} className="cmt-collapse mb-20" expandIconPosition="right">
            <Panel header={<strong>{t('unifiedSearchFilters.baseFiltersTitle')}</strong>} key="1" forceRender={true}>
              {renderBaseFilters()}
            </Panel>
            {hasCustomFilters && (
              <Panel header={<strong>{t('unifiedSearchFilters.customFiltersTitle', { moduleName })}</strong>} key="2" forceRender={true}>
                {renderCustomFilters()}
              </Panel>
            )}
          </Collapse>
        ) : (
          <div className="non-collapsible-filters p-20 mb-20">
            <h3 className="mb-20"><strong>{t('unifiedSearchFilters.baseFiltersTitle')}</strong></h3>
            {renderBaseFilters()}
            {hasCustomFilters && (
              <>
                <h3 className="mb-20 mt-20"><strong>{t('unifiedSearchFilters.customFiltersTitle', { moduleName })}</strong></h3>
                {renderCustomFilters()}
              </>
            )}
          </div>
        )}
        <Row justify="end" align="middle" gutter={15}>
          <Col>
            <Button type="link" onClick={handleClear} className="clear-filters-btn">
              {t('unifiedSearchFilters.clearFilters')}
            </Button>
          </Col>
          <Col>
            <Button type="primary" htmlType="submit" className="cmt-button primary m-0" loading={loading}>
              {t('unifiedSearchFilters.search')}
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default UnifiedSearchFilters;
